"use client";

import { useAppDispatch, useAppSelector } from "@v2/lib/redux/hooks";
import { usePathname } from "next/navigation";
import { Fragment, useEffect } from "react";
import { clearClientCart, updateCart, updateClientCart } from "./store/slices/cart/cartSlice";
import { getCartThunk } from "./store/slices/cart/cartThunk";
import { storeSessionToCsr } from "./store/slices/general";
import { clearClientTryCart, updateClientTryCart, updateTryCart } from "./store/slices/try-cart/tryCartSlice";
import { getTryCartThunk } from "./store/slices/try-cart/tryCartThunk";

function isVideoEmbedPath(pathname: string) {
    return /^\/fa(?:\/v2)?\/videos\/[^/]+\/embed\/?$/.test(pathname);
}

export default function Bootstrap() {
    const dispatch = useAppDispatch()
    const sessionLoaded = useAppSelector(s => s.general.sessionLoaded)
    const isAuthenticated = useAppSelector(s => s.general.session?.isAuthenticated) ?? false
    const pathname = usePathname()
    const shouldSkipBootstrap = isVideoEmbedPath(pathname)

    useEffect(() => {
        if (shouldSkipBootstrap) return;

        fetch('/api/auth/session')
            .then(r => r.json())
            .then(session => {
                if (session?.data?.accessToken) {
                    localStorage.setItem('accessToken', session.data.accessToken)
                }
                dispatch(storeSessionToCsr(session))
            })
            .catch(() => {
                dispatch(storeSessionToCsr({ data: null, isAuthenticated: false }))
            })
    }, [dispatch, shouldSkipBootstrap])


    useEffect(() => {
        if (shouldSkipBootstrap) return;
        // Until the session is known we cannot tell which cart is the real
        // one, and loading the guest cart early flashes its count.
        if (!sessionLoaded) return;

        const updateClientCartEvent = () => {
            const cartInLocalStorage = localStorage.getItem('client_cart')
            const payload = (cartInLocalStorage !== "undefined" && cartInLocalStorage !== "null" && cartInLocalStorage)
                ? JSON.parse(cartInLocalStorage)
                : null;

            if (payload) {
                dispatch(updateClientCart(payload))
            }

            const tryCartInLocalStorage = localStorage.getItem('client_try_cart')
            const tryPayload = (tryCartInLocalStorage !== "undefined" && tryCartInLocalStorage !== "null" && tryCartInLocalStorage)
                ? JSON.parse(tryCartInLocalStorage)
                : null;
            if (tryPayload) {
                dispatch(updateClientTryCart(tryPayload))
            }
        }

        const updateServerCartEvent = () => {

            const cartInLocalStorage = localStorage.getItem('server_cart')
            const payload = (cartInLocalStorage !== "undefined" && cartInLocalStorage !== "null" && cartInLocalStorage)
                ? JSON.parse(cartInLocalStorage)
                : null;
            if (payload) {
                dispatch(updateCart(payload))
            }

            const tryCartInLocalStorage = localStorage.getItem('server_try_cart')
            const tryPayload = (tryCartInLocalStorage !== "undefined" && tryCartInLocalStorage !== "null" && tryCartInLocalStorage)
                ? JSON.parse(tryCartInLocalStorage)
                : null;
            if (tryPayload) {
                dispatch(updateTryCart(tryPayload))
            }
        }

        if (isAuthenticated) {
            // A logged-in user has no guest cart. Drop any copy left by a
            // failed login sync.
            dispatch(clearClientCart())
            dispatch(clearClientTryCart())
            dispatch(getCartThunk())
            dispatch(getTryCartThunk())
        } else {
            updateClientCartEvent()
        }

        if (isAuthenticated) {
            window.addEventListener("storage", updateServerCartEvent);
            return () => window.removeEventListener("storage", updateServerCartEvent)
        } else {
            window.addEventListener("storage", updateClientCartEvent);
            return () => window.removeEventListener("storage", updateClientCartEvent)
        }
    }, [dispatch, sessionLoaded, isAuthenticated, shouldSkipBootstrap]);

    return (
        <Fragment />
    );
}
